import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

const DEFAULT_STALE_TIMES = {
  spec: 10 * 60 * 1000,
  confidence: 60 * 1000,
  decks: 60 * 1000,
  chatSessions: 30 * 1000,
  revisionDraft: 30 * 60 * 1000,
  revisionPlan: 60 * 1000,
};

const StudyWorkspaceContext = createContext(null);

function getResourceEntry(cacheRef, bucket, key) {
  if (!key) return null;
  return getBucketStore(cacheRef, bucket).get(key) || null;
}

function buildSnapshotKey(scope, key) {
  return `${scope || 'default'}:${key || 'default'}`;
}

function getBucketStore(cacheRef, bucket) {
  const store = cacheRef.current[bucket];
  if (!store) {
    throw new Error(`Unknown study workspace cache bucket: ${bucket}`);
  }
  return store;
}

export function StudyWorkspaceProvider({ children }) {
  const cacheRef = useRef({
    spec: new Map(),
    confidence: new Map(),
    decks: new Map(),
    chatSessions: new Map(),
    revisionDraft: new Map(),
    revisionPlan: new Map(),
  });
  const snapshotRef = useRef(new Map());

  const getStaleTime = useCallback((bucket, ttlMs) => {
    if (Number.isFinite(ttlMs) && ttlMs >= 0) return ttlMs;
    return DEFAULT_STALE_TIMES[bucket] || 0;
  }, []);

  const peekCachedResource = useCallback((bucket, key) => {
    if (!key) return null;
    return getResourceEntry(cacheRef, bucket, key);
  }, []);

  const getCachedResource = useCallback((bucket, key, options = {}) => {
    if (!key) return null;
    const entry = getResourceEntry(cacheRef, bucket, key);
    if (!entry) return null;

    const maxAge = getStaleTime(bucket, options.ttlMs);
    if (!Number.isFinite(entry.updatedAt)) return entry.data ?? null;
    if (Date.now() - entry.updatedAt > maxAge) return null;

    return entry.data ?? null;
  }, [getStaleTime]);

  const setCachedResource = useCallback((bucket, key, data) => {
    if (!key) return;
    const previous = getResourceEntry(cacheRef, bucket, key);
    getBucketStore(cacheRef, bucket).set(key, {
      ...(previous || {}),
      data,
      updatedAt: Date.now(),
      error: null,
      promise: null,
    });
  }, []);

  const clearCachedResource = useCallback((bucket, key) => {
    if (!key) return;
    getBucketStore(cacheRef, bucket).delete(key);
  }, []);

  const updateConfidenceStatus = useCallback((key, nextStatusMap) => {
    if (!key) return;
    getBucketStore(cacheRef, 'confidence').set(key, {
      data: {
        statusMap: { ...(nextStatusMap || {}) },
      },
      updatedAt: Date.now(),
      error: null,
      promise: null,
    });
  }, []);

  const getResourceState = useCallback((bucket, key, options = {}) => {
    const entry = getResourceEntry(cacheRef, bucket, key);
    if (!entry) {
      return {
        data: null,
        error: null,
        loading: false,
        updatedAt: null,
        isFresh: false,
      };
    }

    const maxAge = getStaleTime(bucket, options.ttlMs);
    const updatedAt = Number.isFinite(entry.updatedAt) ? entry.updatedAt : null;
    const isFresh = updatedAt == null ? entry.data != null : (Date.now() - updatedAt <= maxAge);

    return {
      data: entry.data ?? null,
      error: entry.error ?? null,
      loading: Boolean(entry.promise),
      updatedAt,
      isFresh,
    };
  }, [getStaleTime]);

  const runResourceTask = useCallback((bucket, key, task, options = {}) => {
    if (!key) return Promise.resolve(null);

    const {
      force = false,
      ttlMs,
    } = options;

    const existingEntry = getResourceEntry(cacheRef, bucket, key);
    if (!force) {
      if (existingEntry?.promise) {
        return existingEntry.promise;
      }

      const cached = getCachedResource(bucket, key, { ttlMs });
      if (cached != null) {
        return Promise.resolve(cached);
      }
    }

    const nextEntry = {
      ...(existingEntry || {}),
      error: null,
    };
    const store = getBucketStore(cacheRef, bucket);
    const promise = Promise.resolve()
      .then(task)
      .then((data) => {
        const latestEntry = getResourceEntry(cacheRef, bucket, key);
        store.set(key, {
          ...(latestEntry || nextEntry),
          data,
          updatedAt: Date.now(),
          error: null,
          promise: null,
        });
        return data;
      })
      .catch((error) => {
        const latestEntry = getResourceEntry(cacheRef, bucket, key);
        store.set(key, {
          ...(latestEntry || nextEntry),
          error,
          promise: null,
        });
        throw error;
      });

    store.set(key, {
      ...nextEntry,
      promise,
    });

    return promise;
  }, [getCachedResource]);

  const getSnapshot = useCallback((scope, key) => {
    if (!key) return null;
    return snapshotRef.current.get(buildSnapshotKey(scope, key)) || null;
  }, []);

  const setSnapshot = useCallback((scope, key, value) => {
    if (!key) return;
    snapshotRef.current.set(buildSnapshotKey(scope, key), value);
  }, []);

  const clearSnapshot = useCallback((scope, key) => {
    if (!key) return;
    snapshotRef.current.delete(buildSnapshotKey(scope, key));
  }, []);

  const value = useMemo(() => ({
    peekCachedResource,
    getCachedResource,
    getResourceState,
    setCachedResource,
    clearCachedResource,
    updateConfidenceStatus,
    runResourceTask,
    getSnapshot,
    setSnapshot,
    clearSnapshot,
  }), [
    peekCachedResource,
    getCachedResource,
    getResourceState,
    setCachedResource,
    clearCachedResource,
    updateConfidenceStatus,
    runResourceTask,
    getSnapshot,
    setSnapshot,
    clearSnapshot,
  ]);

  return (
    <StudyWorkspaceContext.Provider value={value}>
      {children}
    </StudyWorkspaceContext.Provider>
  );
}

export function useStudyWorkspace() {
  return useContext(StudyWorkspaceContext);
}
